import React, { useCallback, useEffect, useState } from 'react';
import { getAdminProtocolDetail, getAdminProtocolLocations, getAdminProtocols } from '../api/admin';
import styles from './Protocols.module.css';

const pretty = (v) => String(v || '').replace(/_/g, ' ');
const shortId = (id) => (id ? `#${String(id).slice(0, 8).toUpperCase()}` : '—');
const when = (v) => (v ? new Date(v).toLocaleString() : '—');

const AREA_LABELS = {
  exterior: 'Exterior',
  windows: 'Windows',
  tires_rims: 'Tires and rims',
  interior: 'Interior',
  vehicle_data: 'Vehicle data',
  accessories: 'Accessories',
};

const DAMAGE_STATUS_BADGE = {
  pre_existing: styles.draft,
  new_at_handover: styles.submitted,
  new_at_return: styles.submitted,
};

function Row({ label, children }) {
  return (
    <div className={styles.row}>
      <span className={styles.key}>{label}</span>
      <span className={styles.val}>{children ?? '—'}</span>
    </div>
  );
}

function PhotoThumb({ photo }) {
  const label = photo.photo_type === 'area' ? photo.area_name : photo.photo_type;
  if (!photo.url) {
    return (
      <div className={styles.photo}>
        <div className={styles.photoLabel}>{pretty(label)} (unavailable)</div>
      </div>
    );
  }
  return (
    <a className={styles.photo} href={photo.url} target="_blank" rel="noreferrer">
      <img src={photo.url} alt={pretty(label)} loading="lazy" />
      <span className={styles.photoLabel}>{pretty(label)}</span>
    </a>
  );
}

export default function Protocols() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [locations, setLocations] = useState(null);
  const [locationsError, setLocationsError] = useState('');
  const [locationsLoading, setLocationsLoading] = useState(false);

  const loadList = useCallback(() => {
    const params = {};
    if (typeFilter) params.type = typeFilter;
    getAdminProtocols(params)
      .then((r) => setRows(r.data.protocols || []))
      .catch((e) => setError(e.response?.data?.error || 'Could not load protocols.'));
  }, [typeFilter]);

  useEffect(() => { loadList(); }, [loadList]);

  const open = async (id) => {
    setSelectedId(id);
    setError('');
    setLocations(null);
    setLocationsError('');
    try {
      setSelected((await getAdminProtocolDetail(id)).data);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load protocol.');
    }
  };

  const showLocation = async () => {
    if (!booking?.id) return;
    setLocationsLoading(true);
    setLocationsError('');
    try {
      const r = await getAdminProtocolLocations(booking.id);
      setLocations(r.data.locations || []);
    } catch (e) {
      setLocations(null);
      setLocationsError(
        e.response?.status === 403
          ? 'You do not have permission to view handover/return locations.'
          : (e.response?.data?.error || 'Could not load location data.')
      );
    } finally {
      setLocationsLoading(false);
    }
  };

  const protocol = selected?.protocol;
  const booking = selected?.booking || {};
  const vehicle = booking.vehicles || {};
  const renter = booking.renter || {};
  const photos = selected?.photos || [];
  const areas = protocol?.areas || [];
  const damage = protocol?.damage || [];

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Vehicle Handover / Return Protocols</h1>
      <p className={styles.subtitle}>Immutable inspection, photo and damage evidence for every booking.</p>

      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.filters}>
        <select className={styles.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          <option value="handover">Handover</option>
          <option value="return">Return</option>
        </select>
      </div>

      <div className={styles.layout}>
        <section className={styles.list}>
          {rows.length === 0 && <p className={styles.empty}>No protocols found.</p>}
          {rows.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => open(p.id)}
              className={`${styles.item} ${selectedId === p.id ? styles.itemActive : ''}`}
            >
              <span className={styles.itemTop}>
                <span className={styles.itemType}>{pretty(p.protocol_type)}</span>
                <span className={`${styles.badge} ${styles[p.status] || ''}`}>{pretty(p.status)}</span>
              </span>
              <span className={styles.itemMeta}>
                {shortId(p.booking?.id)} · {[p.booking?.vehicles?.make, p.booking?.vehicles?.model].filter(Boolean).join(' ') || 'Vehicle'}
                {p.booking?.vehicles?.license_plate ? ` · ${p.booking.vehicles.license_plate}` : ''}
                <br />
                {when(p.submitted_at)}
              </span>
            </button>
          ))}
        </section>

        <section className={styles.detail}>
          {!protocol && <p className={styles.empty}>Select a protocol to view its evidence.</p>}

          {protocol && (
            <>
              <h2 className={styles.detailTitle}>{pretty(protocol.protocol_type)} protocol</h2>

              <Row label="Booking">{shortId(booking.id)}</Row>
              <Row label="Booking status">{pretty(booking.status)}</Row>
              <Row label="Protocol status">{pretty(protocol.status)}</Row>
              <Row label="Vehicle">{[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}</Row>
              <Row label="License plate">{vehicle.license_plate}</Row>
              <Row label="Renter">{renter.full_name}</Row>
              <Row label="Odometer">{protocol.odometer_km != null ? `${protocol.odometer_km} km` : null}</Row>
              <Row label="Fuel / battery">{protocol.fuel_level_percent != null ? `${protocol.fuel_level_percent}%` : null}</Row>
              <Row label="Confirmation checkbox">{protocol.confirmation_consent ? 'Confirmed by renter' : 'Not confirmed'}</Row>
              <Row label="Verified at">{when(protocol.verified_at)}</Row>
              {protocol.is_immutable && <p className={styles.locked}>This report is finalized and immutable.</p>}

              {protocol.notes && (
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>Notes</h3>
                  <p className={styles.damageText}>{protocol.notes}</p>
                </div>
              )}

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Inspection areas ({areas.length})</h3>
                {areas.length === 0 && <p className={styles.empty}>No inspection data yet.</p>}
                {areas.map((a) => (
                  <div key={a.area_name} className={styles.damage}>
                    <div className={styles.damageTop}>
                      <span className={styles.damageType}>{AREA_LABELS[a.area_name] || pretty(a.area_name)}</span>
                      <span className={`${styles.badge} ${a.area_ok === true ? styles.verified : a.area_ok === false ? styles.submitted : styles.draft}`}>
                        {a.area_ok === true ? 'OK' : a.area_ok === false ? 'Issue reported' : 'Pending'}
                      </span>
                    </div>
                    {a.notes && <p className={styles.damageText}>{a.notes}</p>}
                  </div>
                ))}
              </div>

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Damage ({damage.length})</h3>
                {damage.length === 0 && <p className={styles.empty}>No damage recorded.</p>}
                {damage.map((d) => (
                  <div key={d.id} className={styles.damage}>
                    <div className={styles.damageTop}>
                      <span className={styles.damageType}>{pretty(d.damage_type)}{d.vehicle_position ? ` · ${d.vehicle_position}` : ''}</span>
                      <span className={`${styles.badge} ${DAMAGE_STATUS_BADGE[d.status] || ''}`}>{pretty(d.status)}</span>
                    </div>
                    {d.description && <p className={styles.damageText}>{d.description}</p>}
                  </div>
                ))}
              </div>

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Photos ({photos.length})</h3>
                {photos.length === 0 && <p className={styles.empty}>No photos uploaded.</p>}
                <div className={styles.photos}>
                  {photos.map((p) => (
                    <PhotoThumb key={p.id} photo={p} />
                  ))}
                </div>
              </div>

              <div className={styles.section}>
                <h3 className={styles.sectionTitle}>Handover / return location</h3>
                <p className={styles.damageText}>
                  Visible only to admins with the "view protocol locations" permission.
                </p>
                {locations === null && !locationsError && (
                  <button type="button" className={styles.select} onClick={showLocation} disabled={locationsLoading}>
                    {locationsLoading ? 'Loading…' : 'Show captured location'}
                  </button>
                )}
                {locationsError && <p className={styles.error}>{locationsError}</p>}
                {locations && locations.length === 0 && <p className={styles.empty}>No location was captured.</p>}
                {locations && locations.map((loc) => (
                  <Row key={loc.id} label={pretty(loc.protocol_type)}>
                    {loc.gps_lat}, {loc.gps_lng}
                    {loc.accuracy_meters != null ? ` (±${loc.accuracy_meters}m)` : ''} · {when(loc.captured_at)}
                  </Row>
                ))}
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
